import type { AcademicYearSummary } from '@school-erp/types';
import { createTermSchema } from '@school-erp/validation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../api/http';
import { useAuth } from '../../auth/AuthContext';
import { Notice, errorMessage, primaryButton } from '../../components/common';
import { TextField } from '../../components/TextField';
import { t } from '../../i18n';

interface Term {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
}

export function TermsTab({ schoolId }: { schoolId: string }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ name: '', startDate: '', endDate: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);

  const years = useQuery({
    queryKey: ['schools', schoolId, 'years'],
    queryFn: () => api<AcademicYearSummary[]>(`/schools/${schoolId}/academic-years`),
  });
  const year = years.data?.find((y) => y.isCurrent);
  const base = `/schools/${schoolId}/academic-years/${year?.id}/terms`;
  const terms = useQuery({
    queryKey: ['terms', schoolId, year?.id],
    queryFn: () => api<Term[]>(base),
    enabled: !!year,
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['terms', schoolId] });

  const create = useMutation({
    mutationFn: (body: object) => api(base, { method: 'POST', body }),
    onSuccess: () => {
      setForm({ name: '', startDate: '', endDate: '' });
      setMessage(null);
      void refresh();
    },
    onError: (e) => setMessage(errorMessage(e)),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api(`${base}/${id}`, { method: 'DELETE' }),
    onSuccess: refresh,
    onError: (e) => setMessage(errorMessage(e)),
  });

  if (years.isPending) return <p role="status">{t('common.loading')}</p>;
  if (!year) return <Notice kind="error">{t('acad.noYear')}</Notice>;

  return (
    <section aria-label={t('acad.tab.terms')} className="space-y-4">
      <p className="text-sm text-slate-600">
        {t('acad.currentYear')}: <strong>{year.name}</strong> ({year.startDate} → {year.endDate})
      </p>
      {message && <Notice kind="error">{message}</Notice>}
      {can('terms.create', schoolId) && (
        <form
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            const parsed = createTermSchema.safeParse(form);
            if (!parsed.success)
              return setErrors(
                Object.fromEntries(parsed.error.issues.map((i) => [String(i.path[0]), i.message])),
              );
            setErrors({});
            create.mutate(parsed.data);
          }}
          className="grid gap-4 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-4"
        >
          <TextField
            label={t('acad.name')}
            value={form.name}
            error={errors.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <TextField
            label={t('acad.start')}
            type="date"
            value={form.startDate}
            error={errors.startDate}
            onChange={(e) => setForm({ ...form, startDate: e.target.value })}
          />
          <TextField
            label={t('acad.end')}
            type="date"
            value={form.endDate}
            error={errors.endDate}
            onChange={(e) => setForm({ ...form, endDate: e.target.value })}
          />
          <div className="self-end">
            <button type="submit" className={primaryButton}>
              {t('acad.addTerm')}
            </button>
          </div>
        </form>
      )}
      {terms.data?.length === 0 && <p className="text-slate-600">{t('acad.noTerms')}</p>}
      <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white text-sm">
        {terms.data?.map((term) => (
          <li key={term.id} className="flex items-center justify-between p-3">
            <span>
              <span className="font-medium">{term.name}</span>{' '}
              <span className="text-slate-600">
                {term.startDate} → {term.endDate}
              </span>
            </span>
            {can('terms.delete', schoolId) && (
              <button
                type="button"
                className="rounded border border-red-300 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50"
                onClick={() =>
                  window.confirm(t('common.confirmDelete', { name: term.name })) &&
                  remove.mutate(term.id)
                }
              >
                {t('acad.delete')}
              </button>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
